// MARK:- Bundle PDP progress and optional size lock; the theme picker owns all purchasing state.
// Each definition is guarded because several blocks and the fallback section all load this asset.
// Progress re-syncs by fetching its data-section-id (the main product section in block mode) and reading the fresh <custom-volume-progress>.
if (!customElements.get('custom-volume-progress')) {
  class CustomVolumeProgress extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.syncToken = (this.syncToken || 0) + 1;
      this.tiers = this.dataset.tiers.split(',').map((pair) => {
        const [qty, pct] = pair.split(':').map(Number);
        return { qty, pct };
      }).filter(({ qty, pct }) => qty > 0 && pct > 0).sort((a, b) => a.qty - b.qty);
      if (!this.tiers.length) return;
      this.count = this.querySelector('[data-volume-count]');
      this.cue = this.querySelector('[data-volume-cue]');
      this.note = this.querySelector('[data-volume-staged-note]');
      this.rail = this.querySelector('.custom-volume-progress__rail');
      this.ticks = this.querySelectorAll('[data-volume-tier]');
      this.readCart(this);
      this.render();
      this.preserveBackLink();
      const { signal } = this.events;
      const schedule = () => this.scheduleSync();
      for (const name of ['on:cart:add', 'on:line-item:change', 'on:cart-items:refresh']) {
        document.addEventListener(name, schedule, { signal });
      }
      document.addEventListener('shopify:cart:lines-update', async (evt) => {
        // MARK:- Standard cart events fire before the mutation, not after it.
        if (evt.promise) {
          try {
            await evt.promise;
          } catch {
            return;
          }
        }
        if (!signal.aborted) this.scheduleSync();
      }, { signal });
      window.addEventListener('pageshow', (evt) => {
        if (evt.persisted) {
          this.render();
          this.scheduleSync();
        }
      }, { signal });
    }

    readCart(root) {
      const quantities = JSON.parse(root.dataset.cartQty);
      const count = Number(root.dataset.cartCount);
      if (!quantities || Array.isArray(quantities) || typeof quantities !== 'object'
        || !Number.isSafeInteger(count) || count < 0) throw new Error('Invalid bundle cart state');
      this.cartQty = quantities;
      this.cartCount = count;
    }

    stagedCount() {
      // MARK:- Storage can be blocked or malformed. Never write or treat stored caps as live inventory.
      try {
        const stored = window.theme?.storageUtil?.get(this.dataset.storageKey, true, true);
        if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return 0;
        let total = 0;
        for (const [id, meta] of Object.entries(stored)) {
          if (!/^\d+$/.test(id) || !meta || !Number.isSafeInteger(meta.qty) || meta.qty <= 0) continue;
          let qty = meta.qty;
          if (meta.max != null) {
            if (!Number.isSafeInteger(meta.max) || meta.max < 0) continue;
            qty = Math.min(qty, Math.max(0, meta.max - (Number(this.cartQty[id]) || 0)));
          }
          if (Number.isSafeInteger(total + qty)) total += qty;
        }
        return total;
      } catch {
        return 0;
      }
    }

    copy(template, values) {
      return (template || '').replace(/==([a-z]+)==/g, (token, key) => values[key] ?? token);
    }

    // Same ordinal table as custom-volume-savings.js, kept local so the PDP loads no helper.
    static ordinalBox(k) {
      if (k <= 1) return 'box';
      if (k === 2) return '2nd box';
      if (k === 3) return '3rd box';
      return `${k}th box`;
    }

    render() {
      const staged = this.stagedCount();
      const total = this.cartCount + staged;
      const max = this.tiers[this.tiers.length - 1];
      const next = this.tiers.find((tier) => tier.qty > total);
      const values = {
        cart: this.cartCount,
        staged,
        count: next ? next.qty - total : 0,
        percent: next ? next.pct : max.pct,
        box: next ? CustomVolumeProgress.ordinalBox(Math.floor(next.qty / this.tiers[0].qty)) : 'box',
        noun: this.cartCount === 1 ? this.dataset.nounSingular : this.dataset.nounPlural
      };
      const cartText = this.copy(this.dataset.countText, values);
      const stagedText = staged ? this.copy(this.dataset.stagedText, {
        ...values, noun: staged === 1 ? this.dataset.nounSingular : this.dataset.nounPlural
      }) : '';
      const countText = [cartText, stagedText].filter(Boolean).join(' ');
      const cueText = this.copy(next ? this.dataset.nextText : this.dataset.maxText, values);
      // Only mutate changed text to avoid repeating identical live-region announcements.
      if (this.count.textContent !== countText) this.count.textContent = countText;
      if (this.cue.textContent !== cueText) this.cue.textContent = cueText;
      this.note.hidden = staged === 0;
      this.rail.style.setProperty('--progress', `${Math.min(100, total / max.qty * 100)}%`);
      for (const tick of this.ticks) {
        tick.classList.toggle('is-reached', total >= Number(tick.dataset.volumeTier));
      }
    }

    preserveBackLink() {
      const link = this.querySelector('[data-volume-back]');
      if (!link || !document.referrer) return;
      try {
        const referrer = new URL(document.referrer);
        const collection = new URL(link.href);
        if (referrer.origin === location.origin && referrer.pathname === collection.pathname) {
          link.href = referrer.href;
        }
      } catch { /* Invalid referrers leave the Liquid collection URL intact. */ }
    }

    scheduleSync() {
      if (!this.isConnected) return;
      // Invalidate in-flight responses immediately, including during the debounce window.
      this.syncToken += 1;
      this.fetchController?.abort();
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.sectionSync(), 150);
    }

    async sectionSync() {
      const token = ++this.syncToken;
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      try {
        const url = new URL(location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), { signal: this.fetchController.signal });
        if (!response.ok) throw new Error(`Section fetch ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('custom-volume-progress');
        if (token !== this.syncToken || !this.isConnected) return;
        if (!fresh) throw new Error('Bundle progress section missing');
        this.readCart(fresh);
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError' && token === this.syncToken && this.isConnected) {
          console.error('custom-volume-progress: keeping previous cart state', error);
        }
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.fetchController?.abort();
      clearTimeout(this.syncTimer);
      this.syncToken += 1;
    }
  }
  customElements.define('custom-volume-progress', CustomVolumeProgress);
}

if (!customElements.get('custom-volume-option')) {
  class CustomVolumeOption extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.initialize(this.events.signal);
    }

    async initialize(signal) {
      this.section = this.closest('.shopify-section');
      this.picker = this.section?.querySelector('variant-picker');
      if (!this.picker) return;
      // MARK:- A missing picker asset must never leave the native UI hidden.
      await customElements.whenDefined('variant-picker');
      if (signal.aborted || !this.isConnected) return;
      this.button = this.section.querySelector('.js-product-form-main [name="add"]');
      this.badge = this.section.querySelector('[data-volume-media-badge]');
      this.note = this.querySelector('[data-volume-note]');
      this.noteText = this.querySelector('[data-volume-note-text]') || this.note;
      this.check = this.querySelector('[data-volume-check]');
      this.heading = this.querySelector('[data-volume-heading]');
      this.origin = this.querySelector('[data-volume-origin]');
      this.tile = this.querySelector('[data-volume-tile]');
      this.revealButton = this.section.querySelector('[data-volume-reveal]');
      this.originalLabel = this.originalLabel ?? this.button?.dataset.addToCartText;
      // MARK:- The native picker is never collapsed; the tile, heading and reveal button stay hidden.
      this.revealed = true;
      if (!this.picker.id) this.picker.id = `custom-volume-picker-${this.dataset.sectionId}`;
      this.revealButton?.setAttribute('aria-controls', this.picker.id);
      this.section.addEventListener('click', (evt) => {
        if (evt.target.closest('[data-volume-reveal]') !== this.revealButton || !this.revealButton) return;
        this.reveal();
      }, { signal });
      this.section.addEventListener('on:variant:change', (evt) => {
        if (evt.target !== this.picker) return;
        this.applyState(evt.detail?.variant);
      }, { signal });
      this.applyState(this.picker.variant);
      this.updateReveal();
      this.setAttribute('data-ready', '');
    }

    applyState(variant) {
      const locked = String(variant?.id || '') === this.dataset.lockedId;
      this.noteText.textContent = locked ? this.dataset.noteLocked : this.dataset.noteOther;
      if (this.check) this.check.hidden = !locked;
      // MARK:- The bundle note and progress block follow the selected variant; no navigation.
      this.note.hidden = !locked;
      const progress = this.section.querySelector('[data-volume-progress-block]');
      if (progress) progress.hidden = !locked;
      if (this.badge) this.badge.hidden = !locked;
      this.origin.disabled = !locked;
      if (!this.button) return;
      // MARK:- Dataset is the contract even if the picker initializes after this callback.
      const label = locked ? this.dataset.atcLabel : this.originalLabel;
      this.button.dataset.addToCartText = label;
      if (variant?.available) this.button.textContent = label;
      // Never overwrite disabled state, Sold out, Unavailable, or selection-required text.
    }

    updateReveal() {
      this.classList.toggle('is-revealed', this.revealed);
      this.tile.hidden = this.revealed;
      if (this.heading) this.heading.hidden = this.revealed;
      if (this.revealButton) {
        this.revealButton.hidden = this.revealed;
        this.revealButton.setAttribute('aria-expanded', String(this.revealed));
      }
      if (this.revealed) {
        const selector = this.picker.querySelectorAll('.option-selector')[Number(this.dataset.optionIndex) || 0];
        for (const input of selector?.querySelectorAll('input.js-option') || []) {
          if (input.value !== this.dataset.lockedOptionValue) continue;
          const label = input.labels?.[0];
          // MARK:- The theme owns ::after for the selection outline. Use real content for the badge.
          if (label && this.dataset.pillText) {
            let pill = label.querySelector('[data-volume-option-pill]');
            if (!pill) {
              pill = document.createElement('span');
              pill.className = 'custom-volume-option__picker-pill';
              pill.setAttribute('data-volume-option-pill', '');
              label.append(pill);
            }
            pill.textContent = this.dataset.pillText;
          }
        }
      }
    }

    reveal() {
      this.revealed = true;
      this.updateReveal();
      const control = this.picker.querySelector('input.js-option:checked:not(:disabled), custom-select button:not(:disabled), input.js-option:not(:disabled)');
      if (control) control.focus();
      else {
        this.picker.setAttribute('tabindex', '-1');
        this.picker.focus();
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.removeAttribute('data-ready');
      if (this.button && this.originalLabel !== undefined) {
        const ownLabel = this.button.textContent === this.dataset.atcLabel;
        this.button.dataset.addToCartText = this.originalLabel;
        if (ownLabel && !this.button.disabled) this.button.textContent = this.originalLabel;
      }
      if (this.origin) this.origin.disabled = true;
      if (this.revealButton) this.revealButton.hidden = true;
      if (this.badge) this.badge.hidden = true;
    }
  }
  customElements.define('custom-volume-option', CustomVolumeOption);
}
